import { Box, Button, Container, Flex, Heading, Text } from '@chakra-ui/react';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import { GlocalContactModule } from 'models';
import { useRouter } from 'next/router';
import React, { FC, ReactNode } from 'react';
import { withTranslations } from 'store/translations';
import { hrefLink } from 'utils/normalization/hrefLink';

import { getColorScheme } from '../../lib/colorScheme';
import { ContactGroupItemLandscape } from '../pages/ContactUs/ContactLandscape';

interface ContactsModule {
  model: GlocalContactModule;
}

export const GlocalContactsModule: FC<ContactsModule> = ({ model }) => {
  const router = useRouter();
  const languageCodename = router.locale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE!;
  const languageArray = ['en', 'da-dk', 'fi-fi', 'de-de', 'no-no', 'sv-se', 'en-apac', 'en-us', 'en-gb'];
  const localeCodeMap = {
    'en-gb': 'united_kingdom',
    'da-dk': 'denmark',
    'fi-fi': 'finland',
    'no-no': 'norway',
    'de-de': 'germany',
    'sv-se': 'sweden',
  };

  const intersection = languageArray.filter((element) => router.asPath.split('/').includes(element)).join('');
  const calLangCodename = router.asPath.split('/').includes('editors-only') ? intersection : languageCodename;
  const translations = withTranslations();
  const isGlobalLang = calLangCodename == 'en';
  const locationUrlSlug = translations.location(localeCodeMap[calLangCodename]);
  const formattedLocation =
    locationUrlSlug && locationUrlSlug.length ? locationUrlSlug.toLowerCase().replace(' ', '-') : '';
  const headline = model.elements?.headline?.value;
  const textColorScheme = getColorScheme(
    model.elements.backgroundColor?.value[0]?.codename,
    model.elements.headlineColor?.value[0]?.codename,
  );
  const glocalContacts = model.elements?.contacts?.linkedItems;
  const contactModal = model.elements?.formModal?.linkedItems[0];
  const bodyHtml = createRichTextHtmlResolver(nodeParser).resolveRichText({
    element: model.elements.body,
  }).html;
  const isMapPage = model.elements?.officePageChoice?.value[0]?.codename == 'office_map' ? true : false;
  const isApacFilter = model.elements?.officePageChoice?.value[0]?.codename === 'apac';
  const isUsaFilter = model.elements?.officePageChoice?.value[0]?.codename === 'usa';
  const ctaLink = model.elements?.ctaText?.value;
  const disableBtmMargin = model.elements?.removeModuleBottomSpacing?.value[0]?.codename == 'yes' ? true : false;
  let ctaHref = `/${calLangCodename}/${translations.multiple('contactUsUrl')}${
    formattedLocation && formattedLocation.length ? `/${formattedLocation}` : ''
  }${isGlobalLang && isMapPage ? `/${translations.common('map').toLowerCase()}` : ''}`;

  if (isApacFilter) {
    ctaHref = `/en-apac/${translations.multiple('contactUsUrl')}?filter=apac`;
  } else if (isUsaFilter) {
    ctaHref = `/en-us/${translations.multiple('contactUsUrl')}?filter=americas`;
  }
  const ctaHrefNoLocale = ctaHref.replace(`/${calLangCodename}`, '');
  const isCircularLink = router.asPath.indexOf(ctaHrefNoLocale) > -1;

  return (
    <Box
      id={model.system.codename}
      w='100vw'
      bgColor={textColorScheme.background}
      mb={disableBtmMargin ? {} : { base: 's', lg: 'm' }}
    >
      <Container
        pt={{ base: 'm', md: 'm', lg: 'l' }}
        pb={{ base: 'm', md: 'm', lg: 'l' }}
        px={{ base: 'xs', md: 'm', lg: 'l' }}
      >
        <Flex direction={{ base: 'column', md: 'row', lg: 'row' }} gap={{ base: 'm', lg: 'xl' }}>
          <Box flexBasis={'60%'} display='flex' flexDir='column' gap='s'>
            <Text
              as='h1'
              color='mountain.base'
              //flex={{ base: 'unset', lg: '1' }}

              textStyle={{ base: 'mobile.h3', lg: 'desktop.h3_gcontact' }}
              mb={!isCircularLink && ctaLink ? {} : { base: 'm' }}
            >
              {headline}
            </Text>
            <Text
              textStyle={{ base: 'mobile.body.s_g_contact', lg: 'desktop.body.l_gcontact' }}
              // fontWeight={300}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
            {!isCircularLink && ctaLink && (
              <Button
                id='contact-module-button'
                p={'20px'}
                fontFamily='HCo Gotham Rounded Medium'
                fontWeight={350}
                fontSize={{ base: '16px', lg: '18px' }}
                lineHeight='20px'
                h={{ base: '44px', lg: '60px' }}
                as='a'
                href={ctaHref}
                borderRadius='4px'
              >
                {ctaLink}
              </Button>
            )}
          </Box>
          <Box flexBasis={'40%'}>
            <Flex direction={'row'} gap={{ base: '20px', lg: '40px' }} flexDirection={'column'}>
              <Text textStyle={{ base: 'mobile.body.s_gcontact', lg: 'desktop.body.medium.l_gcontact' }}>
                {translations.aria('contactOurExperts')}
              </Text>
              {glocalContacts &&
                glocalContacts
                  .filter((item, idx) => idx < 2)
                  .map((contactItem) => <ContactGroupItemLandscape model={contactItem} form={contactModal} />)}
            </Flex>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};
